// Контракт API пульта: настройки Анны и настройки-выборы для «Запуска → Как Анна квалифицирует» (W3-6).
//
// - `GET /api/v1/settings` (любой вошедший) и `PUT /api/v1/settings` (admin; тело — патч, только то, что меняется) →
//   `{ settings }`: настройки целиком (A13). Их форма — схема A9 на сервере (`src/settings/schema.ts`), здесь — только
//   «объект под ключом settings». Неверное значение — 400 `bad_settings` с текстом замечаний A9, журнал — `settings.update`.
// - `GET /api/v1/settings/choices` (любой вошедший) → `SettingChoices`: выборы разделов `SETTING_CHOICE_SECTIONS` из схемы
//   настроек (`choice(...)` → `describeChoices`): путь, раздел, название, пояснение, варианты словами, значение и
//   умолчание. Новый выбор в этих разделах приходит сам — пульт строит строки по ответу. Изменение одного выбора — тот же
//   `PUT /api/v1/settings` с патчем одного пути: `{"qualify": {"outOfKb": "handoff"}}`.
//
// Совместимость: пути, разделы и значения — строки, а не перечисления, лишние поля отбрасываются. Ответ сервера новее
// пульта (новый раздел, новый вариант, новое поле) не роняет пульт старой версии в открытой вкладке «Ответом сервера не
// того формата»: незнакомый раздел пульт показывает группой «Другие правила». Разделы — копия списка маршрута
// (`routes/settings.ts`); что это разделы схемы A9 с выборами, держит `tests/console/contract.test.ts`.
//
// Правило контракта: файл импортирует только `zod` и соседей (`./*.js`), типы — `z.infer`.
import { z } from 'zod';

/** `GET` и `PUT /api/v1/settings`: настройки целиком (форма — схема A9 на сервере). */
export const SettingsResponseSchema = z.object({ settings: z.record(z.string(), z.unknown()) });
export type SettingsResponse = z.infer<typeof SettingsResponseSchema>;

// ───────────────────────────── выборы ─────────────────────────────

/**
 * Разделы настроек, выборы которых пульт показывает в «Как Анна квалифицирует», — по порядку групп. `reply` (план A21,
 * волна 2) — как Анна пишет ответ: режим «по сценарию» или «по брифу роли» и проверки ответа; группа — сразу после
 * разговора с клиентом.
 */
export const SETTING_CHOICE_SECTIONS = ['qualify', 'reply', 'series', 'bitrix'] as const;
export type SettingChoiceSection = (typeof SETTING_CHOICE_SECTIONS)[number];

/** Группы словами: разговор с клиентом, ответ клиенту, автоматика TextBack (раздел склейки серии), задачи и кнопки Битрикса. */
export const SETTING_CHOICE_SECTION_RU: Readonly<Record<SettingChoiceSection, string>> = {
  qualify: 'Разговор с клиентом',
  reply: 'Ответ клиенту',
  series: 'Автоматика TextBack',
  bitrix: 'Битрикс',
};

/** Группа выборов из раздела, которого этот пульт ещё не знает (сервер новее пульта). */
export const SETTING_CHOICE_OTHER_SECTION_RU = 'Другие правила';

/** Вариант выбора: значение настройки и оно же словами. */
export const SettingChoiceOptionSchema = z.object({ value: z.string().min(1), labelRu: z.string().min(1) });
export type SettingChoiceOption = z.infer<typeof SettingChoiceOptionSchema>;

/**
 * Выбор: путь через точку (`qualify.outOfKb`), раздел, название (как в тексте ошибки A9, со строчной буквы), пояснение для
 * владельца (`null` — нет), варианты по порядку схемы, значение сейчас и умолчание.
 */
export const SettingChoiceSchema = z.object({
  path: z.string().min(1),
  section: z.string().min(1),
  labelRu: z.string().min(1),
  hintRu: z.string().nullable(),
  options: z.array(SettingChoiceOptionSchema).min(1),
  value: z.string(),
  defaultValue: z.string(),
});
export type SettingChoice = z.infer<typeof SettingChoiceSchema>;

/** `GET /api/v1/settings/choices`: выборы по порядку разделов и полей схемы. */
export const SettingChoicesSchema = z.object({ choices: z.array(SettingChoiceSchema) });
export type SettingChoices = z.infer<typeof SettingChoicesSchema>;
